// React
import React, { Fragment, useState, useCallback, useEffect } from 'react'
import { compose } from 'redux'

// Components
import BMiniCart, { HostStyles } from 'shop-components/components/MiniCart/styles'
import Lines from 'shop-components/components/MiniCart/components/Lines'
import Period from 'shop-components/components/MiniCart/components/Period'
import Services from 'shop-components/components/MiniCart/components/Services'
import { PoweredBy } from 'shop-components/components/PoweredBy'

// Libraries
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faTimes as farTimes,
  faTriangleExclamation as farTriangleExclamation
} from '@fortawesome/pro-regular-svg-icons'

// Hooks
import { useCart } from 'shop-components/utils/useCart'
import { useTranslation } from 'shared/utils/useTranslation'
import { useOrm } from 'shared/utils/useOrm'
import { useSelector } from 'react-redux'
import { useSettings } from 'shared/utils/useSettings'
import { useIsMobile } from 'shared/utils/useIsMobile'
import { useCannotCheckout } from 'shop-components/utils/useCannotCheckout'
import { useDateValidation } from 'shop-components/utils/useDateValidation'

// Shared
import registerCustomElement from 'shop-components/utils/registerCustomElement'
import withStore from 'shop-components/utils/withStore'
import withTheme from 'shop-components/utils/withTheme'
import ClientActions from 'store/v2/redux/actions/client'
import LineActions from 'back_office/v2/redux/actions/line_actions'
import Format from 'shared/utils/format'

export const MiniCart = () => {
  const { cart, totalPriceAttribute, showPrices, isOverdue, viewCartHref } = useCart()
  const { t } = useTranslation('user')
  const currency = useSelector((state) => state.client.currency)?.toJS()
  const isOpenPicker = useSelector((state) => state.client.modalPickerOpen)
  const isCartOpen = useSelector((state) => state.client.cartOpen)
  const isCheckingOut = useSelector((state) => state.client.checkingOut)
  const locations = useOrm('locations', (locations) => {
    return locations.getAll().sort('name')
  })
  const lines = useOrm('lines', (lines) => {
    return lines.getAll({ id: cart?.lines }).filter(({ product_type: type }) => type !== 'service')
  })
  const isMobile = useIsMobile()
  const cannotCheckout = useCannotCheckout()
  const isDateValid = useDateValidation()
  const showPoweredBy = useSettings('store.show_powered_by')
  const [autoOpenedModal, setAutoOpenedModal] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchLines = async (cartId) => {
      return LineActions.fetchAll({
        apiVersion: 'boomerang',
        include: [
          'item',
          'item.photo',
          'item.bundle_items',
          'item.bundle_items.product',
          'item.bundle_items.product.photo'
        ],
        filter: {
          owner_id: cartId
        }
      })
    }

    if (cart?.id) {
      fetchLines(cart.id).finally(() => {
        setIsLoading(false)
      })
    }
  }, [cart?.id])

  const hasDates = !!(cart?.starts_at && cart?.stops_at)

  const isCheckoutButtonValid = !!(cart?.lines.length && isDateValid && !cannotCheckout && !isOverdue)

  // Needs to live in parent state as Period gets unmounted
  // on some transitions losing state
  const onAutoModal = useCallback(
    (callback) => {
      setAutoOpenedModal(true)
      callback?.()
    },
    [setAutoOpenedModal]
  )

  const setCheckingOut = (e) => {
    e.preventDefault()

    if (ClientActions.evaluateCartOverdue()) return

    ClientActions.setCheckingOut()
  }

  const toggleCart = (cartOpen) => {
    if (isOpenPicker) return
    return ClientActions.toggleCart(cartOpen)
  }

  const viewCart = () => {
    window.location = window.Booqable.decorateLinkForTracking(viewCartHref)
  }

  return (
    <Fragment>
      <HostStyles />
      <BMiniCart open={isCartOpen} portal={!isMobile} data-tid="Mini cart">
        <BMiniCart.Head>
          <BMiniCart.Title>
            <div>{t('user.store.cart_title')}</div>
            <BMiniCart.Close onClick={toggleCart.bind(null, false)}>
              <FontAwesomeIcon icon={farTimes} />
            </BMiniCart.Close>
          </BMiniCart.Title>
          <Period
            cart={cart}
            hasDates={hasDates}
            locations={locations}
            autoOpenedModal={autoOpenedModal}
            onAutoModal={onAutoModal}
          />
        </BMiniCart.Head>
        <BMiniCart.Lines>
          <Lines
            toggleCart={toggleCart}
            cart={cart}
            cannotCheckout={cannotCheckout}
            hasDates={hasDates}
            loading={isLoading}
          />
        </BMiniCart.Lines>
        {lines?.size() > 0 && (
          <BMiniCart.Summary>
            <Services cart={cart} />
            {showPrices && (
              <BMiniCart.CheckoutTotals>
                <BMiniCart.Detail bold>
                  <span>{t('user.store.subtotal')}</span>
                  <BMiniCart.Amount>{Format.money(cart?.get(totalPriceAttribute), currency)}</BMiniCart.Amount>
                </BMiniCart.Detail>

                {cart?.get('deposit_in_cents') > 0 && (
                  <BMiniCart.Detail>
                    <span>{t('user.store.security_deposit')}</span>
                    <BMiniCart.Amount>{Format.money(cart.get('deposit_in_cents'), currency)}</BMiniCart.Amount>
                  </BMiniCart.Detail>
                )}

                {isOverdue && (
                  <BMiniCart.Notice inSummary>
                    <FontAwesomeIcon icon={farTriangleExclamation} size="lg" />
                    {t('user.checkout.dates_in_past')}
                  </BMiniCart.Notice>
                )}
              </BMiniCart.CheckoutTotals>
            )}
            <BMiniCart.CheckoutButtons>
              {viewCartHref && (
                <BMiniCart.Button onClick={viewCart} invert>
                  {t('user.store.view_cart')}
                </BMiniCart.Button>
              )}
              <BMiniCart.Button onClick={setCheckingOut} disabled={!isCheckoutButtonValid || isCheckingOut}>
                {t('user.store.checkout')}
              </BMiniCart.Button>
            </BMiniCart.CheckoutButtons>
            {showPoweredBy && (
              <BMiniCart.PoweredBy>
                <PoweredBy logoWidth="24px" />
              </BMiniCart.PoweredBy>
            )}
          </BMiniCart.Summary>
        )}
      </BMiniCart>
    </Fragment>
  )
}

registerCustomElement('bq-minicart', compose(withStore, withTheme)(MiniCart))

export default MiniCart
